import { Formik, FormikProps } from 'formik';
import React, { useEffect } from 'react';
import { useState } from 'react';
import _ from 'underscore';

import { beaconComponentRegistryEntry, getUserAccountSettingsURL } from '~src/beacon/BeaconComponentRegistryFunctions';
import { DeepLink } from '~src/beacon/deepLink/DeepLink';
import { shouldShowDmAwarenessSetting } from '~src/commonInfra/components/DMAwareness/DmAwarenessUtils';
import { shouldShowPrepConductorAwarenessSetting } from '~src/flow/utils/PrepConductorAwarenessUtils';
import { cssClass } from '~src/style';

import { SessionUserSelectors } from '../../bootstrap/redux/selectors/SessionInfoSelectors';
import { getAppStore } from '../../bootstrap/redux/store/AppStore';
import { Messages } from '../../codegen/Localize';
import { FormikCheckbox } from '../../commonInfra/components/FormikCheckbox';
import { ToasterService, ToastType } from '../../commonInfra/services/ToasterService';
import { IError } from '../../httpClient/serverApi/types/IError';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { ISiteUserInfo } from '../../userManagement/users/models/ISiteUserInfo';
import { updateDataManagementAwarenessAction } from '../actions/UpdateDataManagementAwarenessAction';
import { UserSettings } from '../services/resources/UserSettings';
import { UserSettingsStyle } from '../styles/UserSettingsStyle';
import { shouldDataManagementSettingsBeVisible } from './PreferenceSettings';
import { InlineCancelAndSaveButtons } from './UserSettingEditButtons';


export type DataManagementAwarenessSettingsProps = {
  siteUser: ISiteUserInfo;
  showPrepConductorCheckbox: boolean;
  showCatalogCheckbox: boolean;
}

export interface DataManagementAwarenessSettingsFields {
  isPrepConductorMessagingEnabled: boolean;
  isCatalogMessagingEnabled: boolean;
}

export const dataManagementAwarenessSectionTestId: string = 'userSettings-DataManagementAwareness';
export const prepConductorAwarenessCheckBoxTestId: string = 'PrepConductorDataManagementAwareness-PrepConductorDataManagementAwareness-Enabled';
export const catalogAwarenessCheckBoxTestId: string = 'CatalogDataManagementAwareness-CatalogDataManagementAwareness-Enabled';

const cssPrefix = 'DMAwarenessUserSettings';
const detailsValueNoPaddingBottom = cssClass(cssPrefix, 'details-value', {
  width: '800px',
  fontSize: '12px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

const DEEPLINK_ID = 'data-management-features';

function showButtons(formik: FormikProps<DataManagementAwarenessSettingsFields>, current: DataManagementAwarenessSettingsFields) {
  if (!formik.touched || !formik.isValid || formik.isSubmitting || _.isEqual(formik.values, current)) {
    return false;
  }
  return true;
}


export function DataManagementAwarenessSettings(props: DataManagementAwarenessSettingsProps): JSX.Element {
  const [currentPrepConductorSetting, setCurrentPrepConductorSetting] = useState(false);
  const [currentCatalogSetting, setCurrentCatalogSetting] = useState(false);

  useEffect(() => {
    // Get initial states
    const state = getAppStore().getState();
    if (props.showPrepConductorCheckbox) {
      setCurrentPrepConductorSetting(SessionUserSelectors.showPrepConductorAwareness(state));
    }
    if (props.showCatalogCheckbox) {
      setCurrentCatalogSetting(SessionUserSelectors.showCatalogAwareness(state));
    }
  }, [props.siteUser.id, props.showCatalogCheckbox, props.showPrepConductorCheckbox]);

  function handleSubmit(values: DataManagementAwarenessSettingsFields, user: ISiteUserInfo) {
    if (!props.showPrepConductorCheckbox) {
      values.isPrepConductorMessagingEnabled = null;
    }

    if (!props.showCatalogCheckbox) {
      values.isCatalogMessagingEnabled = null;
    }

    UserSettings.updateDataManagementAwarenessSetting(user.id, values.isPrepConductorMessagingEnabled, values.isCatalogMessagingEnabled)
      .then((result: IResult) => {
        if (props.showPrepConductorCheckbox) {
          setCurrentPrepConductorSetting(values.isPrepConductorMessagingEnabled);
        }

        if (props.showCatalogCheckbox) {
          setCurrentCatalogSetting(values.isCatalogMessagingEnabled);
        }
        const action = updateDataManagementAwarenessAction(values.isPrepConductorMessagingEnabled, values.isCatalogMessagingEnabled);
        getAppStore().dispatch(action);
        ToasterService.instance.pop({ type: ToastType.Success, text: Messages.settingsUpdateSuccess() });
      })
      .catch((error: IError) => {
        ToasterService.instance.pop({ type: ToastType.Error, text: Messages.settingsUpdateFailure() });
      });
  }

  return <Formik
    initialValues={{ isPrepConductorMessagingEnabled: currentPrepConductorSetting, isCatalogMessagingEnabled: currentCatalogSetting }}
    onSubmit={(values: DataManagementAwarenessSettingsFields) => handleSubmit(values, props.siteUser)}
    enableReinitialize={true}
  >
    {
      (formik: FormikProps<DataManagementAwarenessSettingsFields>) => (
        <DeepLink deepLinkId={DEEPLINK_ID}>
          <form onSubmit={formik.handleSubmit}>
            <div style={{ display: 'flex' }}>
                <div data-tb-test-id={dataManagementAwarenessSectionTestId}>
                  <div className={UserSettingsStyle.settingsLabelText}>
                    <h4>{Messages.dataManagementAwarenessSettingTitle()}</h4>
                  </div>
                </div>
                <div className ={UserSettingsStyle.settingsLabelDescription}>
                  <div className={UserSettingsStyle.detailsValue}>
                    {Messages.dataManagementAwarenessSettingDescription()}
                  </div>
                  <div>
                   {
                    props.showCatalogCheckbox &&
                    <div className={props.showPrepConductorCheckbox ? detailsValueNoPaddingBottom : UserSettingsStyle.detailsValue}>
                      <FormikCheckbox
                        containerStyle={{marginLeft: 0}}
                        testId={catalogAwarenessCheckBoxTestId}
                        name='isCatalogMessagingEnabled'
                        disabled={false}
                        label={Messages.dataManagementAwarenessSettingCatalogOption()}/>
                    </div>
                  }
                  </div>
                  <div>
                    {
                    props.showPrepConductorCheckbox &&

                      <div className={UserSettingsStyle.detailsValue}>
                      <FormikCheckbox
                        containerStyle={{marginLeft: 0}}
                        testId={prepConductorAwarenessCheckBoxTestId}
                        name='isPrepConductorMessagingEnabled'
                        disabled={false}
                        label={Messages.dataManagementAwarenessSettingPrepConductorOption()}/>

                      </div>
                    }
                  </div>
                  {
                    showButtons(formik, { isPrepConductorMessagingEnabled: currentPrepConductorSetting, isCatalogMessagingEnabled: currentCatalogSetting }) &&
                    <InlineCancelAndSaveButtons
                      testId='DataManagementAwareness'
                      formik={formik} />
                  }
                </div>
            </div>
        </form>
        </DeepLink>
      )
    }

  </Formik>;
}

export const DataManagementAwarenessSettingBeaconConfig = beaconComponentRegistryEntry({
  command: () =>  Messages.dataManagementAwarenessSettingTitle(),
  deeplinkId: DEEPLINK_ID,
  url: (props) => getUserAccountSettingsURL(props.sessionInfo),
  isAdmin: false,
  tags: () => [ Messages.dataManagementAwarenessSettingDescription(), Messages.userSettings_Preference_Header() ],
  isVisible: () => {
    return shouldDataManagementSettingsBeVisible(true, shouldShowPrepConductorAwarenessSetting(), shouldShowDmAwarenessSetting());
  }
});